import { useEffect, useMemo, useRef, useState } from 'react';
import { client } from './axios';
import { useSearchParams } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import { labels } from './labels';

enum QuestionStatus {
  CorrectAnswer = 'CorrectAnswer',
  WrongAnswer = 'WrongAnswer',
}

export function Quiz() {
  const [question, setQuestion] = useState<any>(null);
  const [activeSession, setActiveSession] = useState<string | null>(null);
  const [answerId, setAnswerId] = useState<number | null>(null);
  const [status, setStatus] = useState<QuestionStatus | null>(null);
  const [language, setLanguage] = useState<'swe' | 'eng'>('swe');
  const [answerCount, setAnswerCount] = useState<number>(0);

  const [searchParams] = useSearchParams();

  const activSessionIntervalRef = useRef<any>(null);
  const questionIntervalRef = useRef<any>(null);

  const track = searchParams.get('track');
  const station = searchParams.get('station');

  const getActiveSession = async () => {
    try {
      const response = await client.get('/gothiacup/session', {
        params: {
          track,
        },
      });

      if (response.data === '') {
        setActiveSession(null);
        setStatus(null);
        setQuestion(null);
        setAnswerCount(0);
        return;
      }

      const { language, session } = response.data;

      if (session !== activeSession && activeSession !== null) {
        setStatus(null);
        setQuestion(null);
        setAnswerCount(0);
      }

      setLanguage(language);
      setActiveSession(session);
    } catch (error) {
      setActiveSession(null);
      setStatus(null);
      setAnswerCount(0);
    }
  };

  const getQuestion = async () => {
    try {
      const response = await client.get('/gothiacup', {
        params: {
          station,
          track,
        },
      });

      if (response.data?.questionId) {
        setQuestion(response.data);
        setStatus(null);

        if (questionIntervalRef.current) {
          clearInterval(questionIntervalRef.current);
          questionIntervalRef.current = null;
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    const cleanup = () => {
      if (activSessionIntervalRef.current) {
        clearInterval(activSessionIntervalRef.current);
      }
    };

    getActiveSession();

    activSessionIntervalRef.current = setInterval(getActiveSession, 5000);

    return cleanup;
  }, [track, activeSession]);

  useEffect(() => {
    const cleanup = () => {
      if (questionIntervalRef.current) {
        clearInterval(questionIntervalRef.current);
      }
    };

    if (!!activeSession && !question && status !== QuestionStatus.CorrectAnswer) {
      getQuestion();

      questionIntervalRef.current = setInterval(getQuestion, 1000);
    }

    return cleanup;
  }, [activeSession, question]);

  const validate = async (answerId: number) => {
    if (status === QuestionStatus.WrongAnswer) return;

    try {
      setStatus(null);
      setAnswerId(answerId);

      await client.post('/gothiacup/validate', null, {
        params: {
          track,
          station,
          questionId: question.questionId,
          answerId,
        },
      });

      setStatus(QuestionStatus.CorrectAnswer);
      setQuestion(null);

      const timeout = setTimeout(() => {
        setAnswerId(null);
        clearTimeout(timeout);
      }, 10000);
    } catch (error) {
      setStatus(QuestionStatus.WrongAnswer);

      const timeout = setTimeout(() => {
        getQuestion();
        clearTimeout(timeout);
      }, 3000);
    } finally {
      setAnswerCount(prev => prev + 1);
    }
  };

  const content = useMemo(() => {
    if (!activeSession) {
      return (
        <div className="w-screen h-screen flex items-center justify-center bg-cover bg-center bg-no-repeat bg-zinc-100">
          <div className="flex flex-col bg-white border rounded-md p-6 gap-4 border-slate-200">
            <label className="font-md text-lg font-semibold">{labels['no_active_session'][language]}</label>
          </div>
        </div>
      );
    }

    if (!!activeSession && status === QuestionStatus.CorrectAnswer) {
      return (
        <div className="w-screen h-screen flex items-center justify-center text-white bg-cover bg-center bg-no-repeat bg-zinc-100">
          <div className=" bg-emerald-500 border flex  flex-col  font-semibold rounded-md p-6 py-4 gap-4 border-slate-200">
            <div className="text-md text-white flex items-center gap-x-2">
              <i className="ri-check-line text-xl "></i>
              {labels['correct_answer'][language]}
            </div>
          </div>
        </div>
      );
    }

    if (answerCount === 3) {
      return (
        <div className="w-screen h-screen flex items-center justify-center bg-cover bg-center bg-no-repeat bg-zinc-100">
          <div className="flex flex-col bg-white border rounded-md p-6 gap-4 border-slate-200">
            <label className="font-md text-lg font-semibold">{labels['no_more_questions'][language]}</label>
          </div>
        </div>
      );
    }

    if (!!activeSession && !question) {
      return (
        <div className="w-screen h-screen flex items-center justify-center bg-cover bg-center bg-no-repeat bg-zinc-100">
          <div className="flex flex-col bg-white border rounded-md p-6 gap-4 border-slate-200">
            <label className="font-semibold text-lg">{labels['no_question_available'][language]}</label>
          </div>
        </div>
      );
    }

    return (
      <div className="flex flex-col bg-white w-[500px] border rounded-md p-6 gap-4 border-slate-200">
        <label className="font-semibold text-lg">{question?.questionLanguage?.find((ql: any) => ql.languageCode === language)?.label}</label>
        <span>{question?.questionLanguage?.find((ql: any) => ql.languageCode === language)?.subLabel}</span>
        {question.questionId === 12896 && (
          <img
            src="https://jirangopub.s3.eu-north-1.amazonaws.com/Files/9962/Skärmbild_2024-06-14_113258.png"
            className="h-auto w-auto max-w-48 my-2 mx-auto"
          />
        )}

        <div className="flex flex-col gap-4">
          {question?.questionOption?.map(({ questionOptionId, questionOptionLanguage }: any) => (
            <button
              key={questionOptionId}
              disabled={status === QuestionStatus.WrongAnswer}
              className={twMerge(
                'bg-white p-4 rounded-lg  border border-slate-200 relative disabled:opacity-50',
                questionOptionId === answerId && status === QuestionStatus.WrongAnswer && 'bg-red-500 text-white'
              )}
              onClick={() => validate(questionOptionId)}
            >
              {questionOptionLanguage?.find((qol: any) => qol.languageCode === language)?.label}
            </button>
          ))}
        </div>
      </div>
    );
  }, [activeSession, question, answerId, status, language, answerCount]);

  if (station !== '1' && station !== '2') {
    return (
      <div>
        <h1>Invalid Station in URL: select '1' or '2'</h1>
      </div>
    );
  }

  if (track !== '1' && track !== '2') {
    return (
      <div>
        <h1>Invalid Track in URL: select '1' or '2'</h1>
      </div>
    );
  }

  return (
    <div className="w-screen h-screen flex items-center justify-center bg-cover bg-center bg-no-repeat bg-zinc-100">
      <img src="https://jirangopub.s3.eu-north-1.amazonaws.com/Files/9962/SEB_Wordmark_RGB_Black.svg" className="h-12 absolute top-8 left-8" alt="SEB" />
      <h1 className="absolute top-10 text-4xl font-bold">
        {labels['track'][language]} {track}
      </h1>
      {content}
    </div>
  );
}
